// =========================================================================
// design-tokens.js — Canonical tokens for CoParent Console.
//
// Single source of truth. Mirrors `/app/memory/DESIGN_SYSTEM.md`.
//
// Usage:
//   import { tokens, tone, surface, type } from "@/lib/design-tokens";
//   <div style={{ color: tokens.text.primary }} />
//   <Pill tone={tone.attention.text} bg={tone.attention.bg} />
//
// IMPORTANT
// ─────────
// • Do NOT introduce hex colours in JSX that don't appear in this file.
// • Light-mode hex codes are the source of truth; dark-mode equivalents
//   live in `/app/frontend/src/index.dark.css` and follow the same names.
// • If a new colour is genuinely needed, add it here first (with a
//   documented intent) before using it anywhere else.
// =========================================================================

export const tokens = {
  // -------------------- Brand ------------------------------------------
  brand: {
    primary:      "#1F9FA4",   // teal — primary CTA / focus / brand
    primaryDark:  "#116B6E",   // hover text / dense surfaces / links
    primaryHover: "#178085",   // filled-button hover
    primarySoft:  "#D6F0EF",   // teal tint background
    primaryBorder:"#A7D8DA",   // teal tint border
    accentLight:  "#3FC1BC",   // landing / marketing accent only
  },

  // -------------------- Surfaces ---------------------------------------
  surface: {
    page:      "#F5F7F8",      // body / app shell background
    card:      "#FFFFFF",      // standard surface
    muted:     "#EEF3F4",      // muted fills (chip rest, skeletons)
    softHover: "#F4FAFA",      // teal-tinted hover for clickable rows
  },

  // -------------------- Border -----------------------------------------
  border: {
    hairline:  "#D9E1E3",      // canonical 1px border on cards / tiles
    soft:      "#EEF3F4",      // very subtle internal divider
    strong:    "#A8B6BB",      // emphasised separators (rare)
  },

  // -------------------- Text -------------------------------------------
  text: {
    primary:   "#2E3A3F",      // body / titles
    secondary: "#6F828A",      // muted / labels / sublines
    tertiary:  "#9DB0B5",      // very quiet placeholders / disabled
    onBrand:   "#FFFFFF",      // text on teal filled surfaces
    linkDark:  "#116B6E",      // links on light surfaces
  },

  // -------------------- Semantic tones ---------------------------------
  // Each tone has: bg (soft fill), border, text, dot (solid accent).
  // Use the SAME object across status pills, banners, KPI tiles, row
  // tints, and section borders so all status communication looks alike.
  tone: {
    positive: {
      bg: "#D6F0EF", border: "#A7D8DA", text: "#0F5658", dot: "#1F9FA4",
      intent: "Settled · you're owed · confirmed",
    },
    attention: {
      bg: "#FBF0DC", border: "#E2C588", text: "#6F4E1E", dot: "#B88A3A",
      intent: "Awaiting payment · awaiting approval · receipt missing · handover today",
    },
    alert: {
      bg: "#FBE6E3", border: "#E8B6AF", text: "#8C1F12", dot: "#C0291D",
      intent: "Disputed · overdue · genuine issue — NEVER decorative",
    },
    info: {
      bg: "#E6ECEF", border: "#B6C5CC", text: "#2E3E48", dot: "#4A6170",
      intent: "Calendar / informational only",
    },
    calm: {
      bg: "#EEF3F4", border: "#D9E1E3", text: "#2E3A3F", dot: "#9DB0B5",
      intent: "Voided · archived · neutral",
    },
    school: {
      bg: "#E9E4F1", border: "#C3B6D9", text: "#3F2D6A", dot: "#6B4F8B",
      intent: "School / education events only",
    },
  },

  // -------------------- Typography -------------------------------------
  // Tailwind class strings — apply them to elements, do NOT mix arbitrary
  // text-sizes outside this list.
  type: {
    pageTitle:     "font-heading text-2xl sm:text-3xl font-medium text-[#2E3A3F]",
    sectionHeading:"font-heading text-lg font-medium text-[#2E3A3F]",
    cardTitle:     "font-heading text-base font-medium text-[#2E3A3F]",
    eyebrow:       "text-xs uppercase tracking-[0.12em] text-[#6F828A]",
    body:          "text-sm text-[#2E3A3F] leading-snug",
    bodyMuted:     "text-sm text-[#6F828A] leading-snug",
    label:         "text-xs text-[#6F828A]",
    caption:       "text-[11px] text-[#6F828A] leading-snug",
    numeric:       "font-mono-data tabular-nums",
    money:         "font-heading font-semibold tabular-nums",
  },

  // -------------------- Spacing & radius -------------------------------
  radius: {
    chip:   "rounded-sm",     // 0.125rem — chips, pills, tags
    card:   "rounded-md",     // 0.375rem — cards, surfaces (default)
    pill:   "rounded-full",   // avatars, status dots, fully-rounded
  },
  shadow: {
    none:   "shadow-none",
    card:   "shadow-sm",      // default surface lift (used sparingly)
  },

  // -------------------- Controls ---------------------------------------
  // Canonical heights so every input, select, and small button is the
  // same size on the same surface.
  controls: {
    fieldHeight:        "h-9",       // 36 px — default form input
    fieldHeightCompact: "h-8",       // 32 px — chips & filter pills
    tapTarget:          "min-h-[44px] min-w-[44px]", // mobile minimum
    paddingX:           "px-3",
    paddingY:           "py-2",
  },
};

// Convenience aliases so destructuring reads naturally:
export const tone    = tokens.tone;
export const surface = tokens.surface;
export const text    = tokens.text;
export const type    = tokens.type;
export const brand   = tokens.brand;
export const border  = tokens.border;

export default tokens;
